<template>
  <div>
    <v-row>
      <v-col cols="12" :lg="customerColumnWidth">
        <customer-selector
          v-model="selectedCustomer"
          outlined
          :is-mobile="isMobile"
          cke-enabled
          :scr-listening="scrListening"
        ></customer-selector>
      </v-col>
      <v-col cols="12" :lg="detailsColumnWidth">
        <v-text-field
          v-if="isBookingGroup"
          v-model="bookingGroupName"
          label="Gruppbokningsnamn"
          outlined
        ></v-text-field>
        <guest-picker
          v-else
          :max-guests="guestLimits.maxGuests"
          :max-adults="guestLimits.maxAdults"
          :max-children="guestLimits.maxChildren"
          :adults="booking && booking.adults"
          :children="booking && booking.children"
          :dogs="booking && booking.dogs"
          @set-adults="setAdults"
          @set-children="setChildren"
          @set-dogs="setDogs"
        ></guest-picker>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        v-if="isSeasonBooking"
        cols="12"
        lg="4"
      >
        <season-picker
          outlined
          :selected-season-id="booking.season?.id"
          :from-year="checkInDate"
          @set-season="setSeason"
        ></season-picker>
      </v-col>
      <v-col
        cols="12"
        :lg="dateColumnWidth"
      >
        <date-range-picker
          v-if="showGroupDatePicker"
          title="In- och utcheckningsdatum"
          prompt-title-first-date="Välj incheckningsdatum"
          prompt-title-second-date="Välj utcheckningsdatum"
          :first-date="checkInDate"
          :second-date="checkOutDate"
          :is-mobile="isMobile"
          :validation="{ hasError: false, errorText: '' }"
          allow-all-dates
          @set-first-date="setFirstDate"
          @set-second-date="setSecondDate"
        ></date-range-picker>
        <v-checkbox
          v-if="isBookingGroup"
          v-model="sameDatesForAllBookings"
          label="Samma datum för hela gruppbokningen"
          hide-details
          class="pa-0 mt-2"
          color="secondary"
          dense
        ></v-checkbox>
        <common-dates-selection
          v-show="!isSeasonBooking && showGroupDatePicker"
          :check-in-date="checkInDate"
          :check-out-date="checkOutDate"
          @set-dates="setDates"
        ></common-dates-selection>
      </v-col>
      <v-col
        v-show="!isSeasonBooking && !isBookingGroup"
        cols="12"
        lg="4"
      >
        <arrival-time-picker
          :selected-time="booking && booking.expected_arrival_time"
          @set-time="setArrivalTime"
        ></arrival-time-picker>
      </v-col>
    </v-row>

    <v-row
      v-if="!isBookingGroup"
      class="pt-5"
    >
      <v-col cols="12">
        <category-picker
          v-model="selectedCategory"
          column-on-mobile
          dense
          :type="settingCategoryFilterDropdown ? 'dropdown' : 'chips'"
        ></category-picker>
      </v-col>
      <v-col
        cols="12"
        :lg="positionColumnWidth"
        class="pt-0"
      >
        <position-picker
          outlined
          :mandatory="isSeasonBooking"
          :show-map-icon="showMapButton"
          :map-is-open="isShowMap"
          :selected-position="booking?.position"
          :selected-category="categoryPickerValue"
          :check-in="checkInDate"
          :check-out="checkOutDate"
          :occupancy="occupancy"
          :dogs="booking?.dogs"
          :existing-booking-id="booking?.id"
          :booking-client-id="booking?.clientId"
          @map-icon-clicked="toggleMap"
          @set-position="setPositionAndZoom"
          @set-validation="setPositionValidation"
        ></position-picker>
      </v-col>
      <v-col
        v-if="showLodgingTypePicker"
        cols="12"
        lg="4"
        class="pt-0"
      >
        <lodging-type-picker
          v-model="selectedLodgingType"
          :lodging-types="booking.category.lodgingTypes"
        ></lodging-type-picker>
      </v-col>
    </v-row>

    <booking-fields v-if="!isBookingGroup && !isSeasonBooking && booking" :booking="booking"></booking-fields>

    <v-row
      v-show="showCancellationPolicyPicker"
      class="my-4"
    >
      <v-col class="pt-0">
        <cancellation-policy-picker
          :policies="availableCancellationPolicies"
          :selected-policy="booking?.cancellationPolicy"
          :check-in-date="checkInDate"
          :booking-id="isExistingBooking ? null : booking?.id"
          :disabled="isExistingBooking"
          :is-open="show"
          :is-booking-group="isBookingGroup"
          :same-cancellation-policy-for-all-bookings="isBookingGroup ? bookingGroup?.sameCancellationPolicyForAllBookings : true"
          @set-policy="setCancellationPolicy"
          @update:same-cancellation-policy-for-all-bookings="updateSameCancellationPolicyForAllBookings"
        ></cancellation-policy-picker>
      </v-col>
    </v-row>

    <v-row
      v-show="isSeasonBooking"
    >
      <v-col class="pt-0">
        <accommodation-price-input
          v-if="!!booking"
          v-model="accommodationPrice"
        ></accommodation-price-input>
      </v-col>
    </v-row>

    <v-row v-if="showFeeOptions" class="mb-4">
      <v-col>
        <booking-fee-options
          v-model="appliedFees"
          :booking="isBookingGroup ? null : booking"
          :booking-group="isBookingGroup ? bookingGroup : null"
          :is-regular-booking="isRegularBooking"
          :is-season-booking="isSeasonBooking"
        ></booking-fee-options>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BookingFields from '@/components/booking/form/BookingFields.vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AccommodationPriceInput from '@/components/booking/AccommodationPriceInput.vue'
import ArrivalTimePicker from '@/components/booking/form/ArrivalTimePicker.vue'
import BookingFeeOptions from '@/components/booking/form/FeeOptions.vue'
import GuestPicker from '@/components/booking/form/GuestPicker.vue'
import SeasonPicker from '@/components/booking/form/SeasonPicker.vue'
import CancellationPolicyPicker from '@/components/cancellation-policy/CancellationPolicyPicker.vue'
import CommonDatesSelection from '@/components/common/CommonDatesSelection.vue'
import DateRangePicker from '@/components/common/DateRangePicker.vue'
import CustomerSelector from '@/components/customer/Selector.vue'
import CategoryPicker from '@/components/position/CategoryPicker.vue'
import LodgingTypePicker from '@/components/position/LodgingTypePicker.vue'
import PositionPicker from '@/components/position/PositionPicker.vue'

export default {
  name: 'BookingFormFieldsSection',
  components: {
    BookingFields,
    AccommodationPriceInput,
    ArrivalTimePicker,
    BookingFeeOptions,
    CancellationPolicyPicker,
    CategoryPicker,
    CommonDatesSelection,
    CustomerSelector,
    DateRangePicker,
    GuestPicker,
    LodgingTypePicker,
    PositionPicker,
    SeasonPicker
  },
  props: {
    showMapButton: Boolean,
    isShowMap: Boolean
  },
  computed: {
    ...mapState('bookingForm', {
      categoryPickerValue: 'categoryPickerValue',
      show: 'show',
      bookingGroup: 'bookingGroupDraft'
    }),
    ...mapGetters('bookingForm', {
      booking: 'activeBooking',
      availableCancellationPolicies: 'availableCancellationPolicies',
      checkInDate: 'checkInDate',
      checkOutDate: 'checkOutDate',
      customer: 'customer',
      isBookingGroup: 'isBookingGroup',
      isExistingBooking: 'isExistingBooking',
      isRegularBooking: 'isRegularBooking',
      isSeasonBooking: 'isSeasonBooking',
      scrEnabled: 'scrEnabled',
      settingCategoryFilterDropdown: 'settingCategoryFilterDropdown',
      showCancellationPolicyPicker: 'showCancellationPolicyPicker',
      showLodgingTypePicker: 'showLodgingTypePicker'
    }),
    selectedCustomer: {
      get: function () {
        return this.customer
      },
      set: function (val) {
        this.updateCustomer(val)
      }
    },
    bookingGroupName: {
      get: function () {
        return this.bookingGroup?.name
      },
      set: function (val) {
        this.updateBookingGroupName(val)
      }
    },
    selectedCategory: {
      get: function () {
        return this.categoryPickerValue
      },
      set: function (val) {
        Promise.resolve(this.updateCategoryPickerValue(val))
          .then((result) => {
            this.$emit('category-updated', { category: val, result })
          })
      }
    },
    selectedLodgingType: {
      get: function () {
        return this.booking?.lodgingType
      },
      set: function (val) {
        this.updateLodgingType(val)
        this.markLodgingTypeSelected() // Markerar att det är användaren som valt boendetyp
      }
    },
    accommodationPrice: {
      get: function () {
        return this.booking?.accommodation_price
      },
      set: function (val) {
        this.updateAccommodationPrice(val)
        this.markAccommodationPriceEdited()
      }
    },
    appliedFees: {
      get: function () {
        return this.isBookingGroup
          ? this.bookingGroup?.appliedFees
          : this.booking?.appliedFees
      },
      set: function (val) {
        this.updateAppliedFees(val)
      }
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    scrListening: function () {
      return this.show && this.scrEnabled
    },
    customerColumnWidth: function () {
      return this.isBookingGroup ? 7 : 8
    },
    detailsColumnWidth: function () {
      return this.isBookingGroup ? 5 : 4
    },
    guestLimits: function () {
      return {
        maxGuests: this.booking?.category?.max_guests ?? 99,
        maxAdults: this.booking?.category?.max_adults ?? 99,
        maxChildren: this.booking?.category?.max_children ?? 99
      }
    },
    dateColumnWidth: function () {
      return this.isBookingGroup ? 12 : 8
    },
    occupancy: function () {
      return this.booking ? this.booking.adults + this.booking.children : null
    },
    positionColumnWidth: function () {
      return this.showLodgingTypePicker ? 8 : 12
    },
    showFeeOptions: function () {
      return this.isBookingGroup ? !!this.bookingGroup : !!this.booking
    },
    showGroupDatePicker: function () {
      return !this.isBookingGroup || this.bookingGroup?.sameDatesForAllBookings
    },
    sameDatesForAllBookings: {
      get: function () {
        return this.bookingGroup?.sameDatesForAllBookings ?? true
      },
      set: function (value) {
        this.updateSameDatesForAllBookings(value)
      }
    }
  },
  methods: {
    ...mapActions('bookingForm', {
      markAccommodationPriceEdited: 'markAccommodationPriceEdited',
      markLodgingTypeSelected: 'markLodgingTypeSelected',
      setArrivalTime: 'setArrivalTime',
      setCancellationPolicy: 'setCancellationPolicy',
      setDates: 'setDates',
      setFirstDate: 'setCheckInDate',
      setSameCancellationPolicyForAllBookings: 'setSameCancellationPolicyForAllBookings',
      setSameDatesForAllBookings: 'setSameDatesForAllBookings',
      setSecondDate: 'setCheckOutDate',
      setSeason: 'setSeason',
      updateAccommodationPrice: 'setAccommodationPrice',
      updateAppliedFees: 'setAppliedFees',
      updateBookingGroupName: 'setBookingGroupName',
      updateCategoryPickerValue: 'setCategoryPickerValue',
      setAdults: 'setAdults',
      setChildren: 'setChildren',
      setDogs: 'setDogs',
      updateCustomer: 'setCustomer',
      updateLodgingType: 'setLodgingType'
    }),
    toggleMap: function () {
      this.$emit('toggle-map')
    },
    setPositionAndZoom: function (position) {
      this.$emit('set-position-and-zoom', position)
    },
    setPositionValidation: function (validation) {
      this.$emit('set-position-validation', validation)
    },
    updateSameCancellationPolicyForAllBookings: function (value) {
      return this.setSameCancellationPolicyForAllBookings(value)
    },
    updateSameDatesForAllBookings: function (value) {
      return this.setSameDatesForAllBookings(value)
    }
  }
}
</script>
